import React, { useState, useEffect } from "react";
import ReactDOM from "react-dom";
import { BrowserRouter } from "react-router-dom";
import App from "./_features/App";
import { config } from "config";

import "./index.css";
import { StorageKeys } from "enums/StorageKeys";
import { PaperCompanies } from "enums/PaperCompanies";

function PaperCompanyValues() {
    const [errorMessage, setErrorMessage] = useState<string | null>(null);
    const [paperCompanyData, setPaperCompanyData] = useState<PaperCompany | null>(null);

    function loadDynamicFavicon() {
        const existingFavicon = document.getElementById(
            "dynamic-favicon"
        ) as HTMLLinkElement | null;
        if (existingFavicon) {
            existingFavicon.remove();
        }

        const favicon = document.createElement("link");
        favicon.id = "dynamic-favicon";
        favicon.rel = "icon";

        const host = window.location.hostname.toLowerCase();

        const isGSL = host.endsWith("gslcustomer.com") || process.env.REACT_APP_IS_GSL === "true";
        const version = Date.now();

        favicon.href = isGSL
            ? `/images/gslfavicon.png?v=${version}`
            : `/images/favicon.ico?v=${version}`;
        document.head.appendChild(favicon);
    }

    function loadDynamicPageTitle(values: any) {
        document.title = values.companyName
            ? `${values.companyName} Customer Portal`
            : "Customer Portal";
    }

    useEffect(() => {
        function fetchWithRetry(
            url: string,
            maxRetries = 2,
            delayMs = 1000
        ): Promise<Response> {
            return fetch(url).then((response) => {
                if (!response.ok) {
                    throw new Error(`HTTP error! status ${response.status}`);
                }
                return response;
            }).catch((error) => {
                if (maxRetries > 0) {
                    console.warn(
                        `Fetch failed for ${url}, retrying (${maxRetries} attempts left)...`,
                        error
                    );
                    return new Promise<Response>((resolve) =>
                        setTimeout(resolve, delayMs)
                    ).then(() => fetchWithRetry(url, maxRetries - 1, delayMs));
                }
                throw error;
            });
        }

        // CDN fetch — writes to sessionStorage for other consumers, sets favicon/title
        const cdnPromise = fetchWithRetry(config.app.paperCompanyCdn)
            .then((response) => response.json())
            .then((data) => {
                const host = window.location.hostname.toLowerCase();
                const companyKey =
                    host.endsWith("gslcustomer.com") || process.env.REACT_APP_IS_GSL === "true"
                        ? PaperCompanies.GSL
                        : "";
                const values =
                    companyKey === PaperCompanies.GSL
                        ? data[companyKey.toLowerCase()]
                        : { ...data };

                if (companyKey !== PaperCompanies.GSL && values.gsl) {
                    delete values.gsl;
                }

                sessionStorage.setItem(
                    StorageKeys.PaperCompanyValues,
                    JSON.stringify(values)
                );
                loadDynamicFavicon();
                loadDynamicPageTitle(values);
            })
            .catch((error) => {
                console.error("Failed to load PaperCompanyValues from CDN after retries:", error);
                loadDynamicFavicon();
            });

        // API fetch — paper company settings (e.g. onlinePaymentsEnabled)
        const paperCompanyPromise = fetchWithRetry(
            config.resourceServer.apiUrl + "/home/getPaperCompanySettings"
        )
            .then((response) => response.json())
            .then((data) => {
                sessionStorage.setItem(StorageKeys.PaperCompanySettings, JSON.stringify(data));
                setPaperCompanyData(data);
            })
            .catch((error) => {
                console.warn("Failed to load PaperCompanySettings from API after retries:", error);
                const fallback = { onlinePaymentsEnabled: false };
                sessionStorage.setItem(
                    StorageKeys.PaperCompanySettings,
                    JSON.stringify(fallback)
                );
                setPaperCompanyData(fallback);
            });

        // Wait for CDN to finish (for favicon/title), then reveal the app
        cdnPromise.then(() => {
            const themeCssPromise = (window as any).__themeCssLoaded || Promise.resolve();
            themeCssPromise.then(() => {
                document.documentElement.setAttribute("data-app-ready", "true");
            });
        });
    }, []);

    if (errorMessage && errorMessage.length > 0) {
        return (
            <div className="row" style={{ padding: 10 }}>
                <div className="col-12">
                    <div>
                        <label className="text-danger" aria-live="assertive" role="alert">
                            {errorMessage}
                            <br />
                            <br />
                        </label>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <BrowserRouter>
            <App paperCompanyData={paperCompanyData} />
        </BrowserRouter>
    );
}

ReactDOM.render(
    <React.StrictMode>
        <PaperCompanyValues />
    </React.StrictMode>,
    document.getElementById("root")
);
